import { Injectable } from '@angular/core';
import { ApiService } from '../../../_core/_services/api.service';
import { NotificationService } from '../../../_share/_services/notification.service';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

// Standard interface for entity services
export interface EntityServiceInterface {
  // Data observables
  data$: BehaviorSubject<any[]>;

  // CRUD operations
  getData(queryParams?: any): Observable<any>;
  addData(data: any): Observable<any>;
  updateData(id: number, data: any): Observable<any>;
  deleteData(id: number): Observable<any>;

  // Refresh data
  refreshData(): void;
  refreshDataWithParams?(queryParams?: any): void;
}

// Entity configuration
export interface EntityConfig {
  endpoint: string;
  displayName: string;
  service: EntityServiceInterface;
  columns?: any[];
  formSchema?: any;
}

@Injectable({
  providedIn: 'root'
})
export class EntityService {
  // Dynamic entity registry
  private entities = new Map<string, EntityConfig>();

  // Current active entity
  private activeEntitySubject = new BehaviorSubject<string | null>(null);
  activeEntity$ = this.activeEntitySubject.asObservable();

  constructor(
    public api: ApiService,
    public notificationService: NotificationService
  ) {}

  // Register an entity
  registerEntity(key: string, config: EntityConfig): void {
    console.log(`=== ENTITY SERVICE REGISTRATION ===`);
    console.log(`Registering entity: ${key}`, config);
    console.log(`Current entities before registration:`, Array.from(this.entities.keys()));
    this.entities.set(key, config);
    console.log(`Current entities after registration:`, Array.from(this.entities.keys()));
    console.log(`Entity ${key} registration complete`);
  }

  // Get entity configuration
  getEntityConfig(key: string): EntityConfig | undefined {
    console.log(`=== ENTITY SERVICE GET CONFIG ===`);
    console.log(`Getting config for entity: ${key}`);
    console.log(`Current entities:`, Array.from(this.entities.keys()));
    const config = this.entities.get(key);
    console.log(`Config found for ${key}:`, !!config);
    return config;
  }

  // Get all registered entities
  getRegisteredEntities(): string[] {
    return Array.from(this.entities.keys());
  }

  // Set active entity
  setActiveEntity(key: string): void {
    this.activeEntitySubject.next(key);
  }

  // Get data for an entity
  getData(entityKey: string, queryParams?: any): Observable<any> {
    const config = this.getEntityConfig(entityKey);
    if (!config) {
      throw new Error(`Entity ${entityKey} not registered`);
    }
    return config.service.getData(queryParams);
  }

  // Add new entity
  addEntity(entityKey: string, data: any): Observable<any> {
    const config = this.getEntityConfig(entityKey);
    if (!config) {
      throw new Error(`Entity ${entityKey} not registered`);
    }

    return config.service.addData(data).pipe(
      map((response) => {
        this.notificationService.successfullyModifiedEntity('create', config.displayName);
        // Note: For add operations, we typically want to refresh with current filters
        // but the component will handle the refresh with preserved state
        return response;
      }),
      catchError((error) => {
        this.notificationService.errorModifyingEntity('create', config.displayName);
        throw error;
      })
    );
  }

  // Update entity
  updateEntity(entityKey: string, id: number, data: any): Observable<any> {
    console.log('=== ENTITY SERVICE UPDATE DEBUG ===');
    console.log('Updating entity:', entityKey, 'with ID:', id);
    console.log('Update data:', data);

    const config = this.getEntityConfig(entityKey);
    if (!config) {
      console.error('Entity config not found for:', entityKey);
      throw new Error(`Entity ${entityKey} not registered`);
    }

    console.log('Entity config found:', config);
    console.log('Service being used:', config.service);
    console.log('Service constructor name:', config.service.constructor.name);

    return config.service.updateData(id, data).pipe(
      map((response) => {
        console.log('=== ENTITY SERVICE UPDATE SUCCESS ===');
        console.log('Update response:', response);
        this.notificationService.successfullyModifiedEntity('update', config.displayName);
        // Note: For update operations, we typically want to refresh with current filters
        // but the component will handle the refresh with preserved state
        return response;
      }),
      catchError((error) => {
        console.log('=== ENTITY SERVICE UPDATE ERROR ===');
        console.error('Update error in EntityService:', error);
        this.notificationService.errorModifyingEntity('update', config.displayName);
        throw error;
      })
    );
  }

  // Delete entity
  deleteEntity(entityKey: string, id: number): Observable<any> {
    const config = this.getEntityConfig(entityKey);
    if (!config) {
      throw new Error(`Entity ${entityKey} not registered`);
    }

    return config.service.deleteData(id).pipe(
      map((response) => {
        this.notificationService.successfullyModifiedEntity('delete', config.displayName);
        // Note: For delete operations, we typically want to refresh with current filters
        // but the component will handle the refresh with preserved state
        return response;
      }),
      catchError((error) => {
        this.notificationService.errorModifyingEntity('delete', config.displayName);
        throw error;
      })
    );
  }

  // Refresh entity data
  refreshEntity(entityKey: string): void {
    const config = this.getEntityConfig(entityKey);
    if (config) {
      config.service.refreshData();
    }
  }

  // Refresh entity data with parameters to preserve state
  refreshEntityWithParams(entityKey: string, queryParams?: any): void {
    const config = this.getEntityConfig(entityKey);
    if (config) {
      // Use the new refreshDataWithParams method if available, otherwise fallback to getData
      if (config.service.refreshDataWithParams) {
        config.service.refreshDataWithParams(queryParams);
      } else {
        config.service.getData(queryParams).subscribe();
      }
    }
  }

  // Get entity data observable
  getEntityData$(entityKey: string): BehaviorSubject<any[]> | null {
    const config = this.getEntityConfig(entityKey);
    return config ? config.service.data$ : null;
  }

  // Get entity columns
  getEntityColumns(entityKey: string): any[] {
    const config = this.getEntityConfig(entityKey);
    return config?.columns || [];
  }

  // Get entity form schema
  getEntityFormSchema(entityKey: string): any {
    const config = this.getEntityConfig(entityKey);
    return config?.formSchema || null;
  }
}
